import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { ListChecks, AlertTriangle, CheckCircle2, ArrowRight } from "lucide-react";

/**
 * Phase 6 — Dashboard tile for the Recurring Checklist module.
 * Counters are role-scoped server-side: super_admin/mis see ALL instances,
 * hr/ea see their templates' instances, regular users see only their own.
 */
export default function ChecklistDashboardTile() {
  const [t, setT] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/checklist/dashboard-tile")
      .then(({ data }) => setT(data))
      .catch(() => setErr(true));
  }, []);

  if (err || !t) return null;
  const total = (t.due_this_week || 0) + (t.overdue || 0);
  if (total === 0 && (t.completed_last_30d || 0) === 0) return null;

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="checklist-dashboard-tile">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">
              Compliance Checklist
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Recurring tasks status</div>
          </div>
          <ListChecks className="h-4 w-4 text-red-500" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Mini label="Due this week" value={t.due_this_week} tone="amber" />
          <Mini label="Overdue" value={t.overdue} tone="red" icon={AlertTriangle} />
          <Mini label="Done · 30d" value={t.completed_last_30d} tone="green" icon={CheckCircle2} />
        </div>
        <Link to="/checklist"
              className="mt-3 inline-flex items-center text-[11px] text-red-700 hover:underline"
              data-testid="checklist-tile-link">
          Open my checklist <ArrowRight className="h-3 w-3 ml-1" />
        </Link>
      </CardContent>
    </Card>
  );
}

function Mini({ label, value, tone, icon: Icon }) {
  const cls = {
    amber: "text-amber-700",
    red: "text-red-700",
    green: "text-emerald-700",
  }[tone];
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-xl font-bold ${cls} flex items-center gap-1`}>
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {value ?? 0}
      </div>
    </div>
  );
}
